import { useDisclosure } from '@chakra-ui/react';
import { AccountTypeEnum, AssociationEntity } from '@monetize/types/app';
import { BillGroupTabTypes } from '@monetize/types/common';
import React, {
  FC,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { MdAccountTree, MdInfo } from 'react-icons/md';
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useGetAccountById } from '../../api/accountsService';
import { AccountPartnerBadge } from '../../components/Account/AccountPartnerBadge';
import { ContactFormModal } from '../../components/Contacts/ContactFormModal';
import ContractModal from '../../components/Contracts/ContractModal';
import { ExternalAssociationsIcons } from '../../components/ExternalAssociations/ExternalAssociationsIcons';
import {
  MButton,
  MCustomIconButton,
  MCustomLinkTab,
  MDropdownActionItem,
  MDropdownActions,
  MFlex,
  MIcon,
  MPageContainer,
  MPageHeader,
  MPageLoader,
  MTabList,
  MTabs,
  MText,
  MTooltip,
} from '../../components/Monetize';
import DataTableActions from '../../components/Monetize/DataTable/MDataTableActions';
import { MPageHeaderLeftSection } from '../../components/Monetize/MPageHeaderLeftSection';
import { NotesDrawer } from '../../components/Notes/NotesDrawer';
import { PastDueBadge } from '../../components/PastDueBadge';
import { AddPaymentMethodForm } from '../../components/PaymentMethods/Form/AddPaymentMethodForm';
import { ROUTES } from '../../constants';
import {
  AccountStatusDisplayText,
  billGroupDetailsTabs,
} from '../../constants/accounts';
import { useShowPastDueFields } from '../../hooks';
import useCurrentPaymentGateways from '../../hooks/useCurrentPaymentGateways';
import { ACLCheck } from '../../services/acl/ACLCheck';
import { useACL } from '../../services/acl/acl';
import { useDocumentHead } from '../../services/documentHead';
import { FeatureFlag, useFlags } from '../../services/launchDarkly';
import { AccountHierarchyModal } from '../Accounts/AccountHierarchy/AccountHierarchyModal';
import { PrivateRoute } from '../AppPrivateRoute';
import { useCanCreateQuote } from '../Quotes/Quote/useCanCreateQuote';
import { AccountDetailsBillGroupFilter } from './AccountDetailsBillGroupFilter';
import { AccountDocumentList } from './AccountDocuments/AccountDocumentList';
import { AccountDocumentFormModal } from './AccountDocuments/components/AccountDocumentFormModal';
import AccountHistoryDrawer from './AccountHistory/AccountHistoryDrawer';
import { AccountMergeModal } from './AccountMergeModal/AccountMergeModal';
import { BillGroupListPage } from './BillGroups/BillGroupListPage';
import { BillGroupPage } from './BillGroups/BillGroupPage';
import ContactList from './Contacts/ContactList';
import ContractListAccount from './Contracts/ContractListAccount';
import { CreditFormCreateModal } from './Credits/Credits/CreditFormCreateModal';
import { CreditsList } from './Credits/CreditsList';
import { AccountDetailsTab } from './Details/AccountDetailsTab';
import AccountInvoices from './Invoices/AccountInvoices';
import AccountOverview from './Overview/AccountOverview';
import { PaymentMethodList } from './PaymentMethods/PaymentMethodList';
import { PaymentMethodModal } from './PaymentMethods/PaymentMethodModal';
import { AccountManualPaymentModal } from './Payments/AccountManualPaymentModal';
import { PaymentList } from './Payments/PaymentList';
import QuoteListAccount from './Quotes/QuoteListAccount';
import { AccountRefundList } from './Refunds/AccountRefundList';
import { SubscriptionList } from './Subscriptions/SubscriptionList';
import { UsagePage } from './Usage/UsagePage';
import { ACCOUNT_TAB_ROUTES } from './constants';

// Cramped tabs wrap their labels, growing upward (`alignItems: flex-end` on
// the list). Most labels are single words, so on narrow screens even wrapped
// labels don't fit; the list then scrolls instead of overflowing the page
// container, which would clip the last tabs.
const ACCOUNT_TAB_LIST_CSS = {
  maxW: 'full',
  overflowX: 'auto',
  overflowY: 'hidden',
  // Room for the triggers' 3px underline overhang, which the scroll
  // container would otherwise clip.
  pb: '3px',
  '& > [role=tab]': {
    pt: 2,
    pb: 3,
    // An outer halo would be clipped by the scroll container.
    _focusVisible: { boxShadow: 'focusInset' },
  },
};

const DropDownMenuItem = ({ children }: React.PropsWithChildren) => (
  <MText color="inherit">{children}</MText>
);
export const AccountDetailsPage: FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const { accountId = '' } = useParams();
  const { setDocTitle } = useDocumentHead();
  const { canDo } = useACL();
  const showPastDue = useShowPastDueFields();
  const canUpdateAccount = canDo([['accounts', 'update']]);
  const canCreateQuote = useCanCreateQuote();

  const {
    open: isAccountMergeModalOpen,
    onOpen: onAccountMergeModalOpen,
    onClose: onAccountMergeModalClose,
  } = useDisclosure();

  const {
    open: isAccountHierarchyModalOpen,
    onOpen: onAccountHierarchyModalOpen,
    onClose: onAccountHierarchyModalClose,
  } = useDisclosure();

  const {
    open: isManualPaymentModalOpen,
    onOpen: onManualPaymentModalOpen,
    onClose: onManualPaymentModalClose,
  } = useDisclosure();

  const featureFlags = useFlags();
  const { enableAccountMerge, accountHierarchy, showSubscriptionOnAccount } =
    featureFlags;

  const billGroupId = searchParams.get('billGroupId') || undefined;
  const { currentPaymentGateway: paymentGateway } = useCurrentPaymentGateways();
  const {
    isLoading,
    data: accountDetails,
    refetch: refetchAccountDetails,
  } = useGetAccountById(accountId, {
    enabled: !!accountId,
    meta: {
      showErrorToast: true,
      navigateTo: ROUTES.ACCOUNT_LIST,
    },
  });

  const [activeTabIndex, setActiveTabIndex] = useState<number>();

  const canCreateBilling = canDo([['billing', 'create']]);
  const canCreateContacts = canDo([['account_contacts', 'create']]);

  const newButtonRef = useRef<HTMLButtonElement>(null);
  const contactItemRef = useRef<HTMLDivElement>(null);
  const creditItemRef = useRef<HTMLDivElement>(null);
  const quoteItemRef = useRef<HTMLDivElement>(null);

  const firstDropdownItemRef = useMemo(() => {
    if (canCreateContacts) {
      return contactItemRef;
    }
    if (canCreateBilling) {
      return creditItemRef;
    }
    if (canCreateQuote) {
      return quoteItemRef;
    }
    return undefined;
  }, [canCreateBilling, canCreateContacts, canCreateQuote]);

  useEffect(() => {
    setDocTitle(accountDetails?.accountName);
    newButtonRef.current?.focus();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountDetails]);

  useLayoutEffect(() => {
    const index = ACCOUNT_TAB_ROUTES.filter(
      ({ requiresFeatureFlag }) =>
        !requiresFeatureFlag ||
        featureFlags[requiresFeatureFlag as keyof FeatureFlag],
    ).findIndex(({ route }) => pathname.includes(route));
    setActiveTabIndex(index);
  }, [featureFlags, pathname]);

  const billGroupTabName = useMemo(() => {
    const currentPath = pathname.split('/').reverse()[0];
    return !currentPath || billGroupDetailsTabs.has(currentPath)
      ? 'details'
      : (currentPath as BillGroupTabTypes);
  }, [pathname]);

  if (isLoading || !accountDetails) {
    return <MPageLoader />;
  }

  const openNewAccountDocument = () => {
    navigate(ROUTES.getAccountDocumentCreate(accountId));
  };
  const openNewQuote = () => {
    navigate(ROUTES.getAccountQuoteNewRoute(accountId));
  };
  const createCredit = () => {
    navigate(ROUTES.getAccountCreditCreate(accountId));
  };
  const createContact = () => {
    navigate(ROUTES.getAccountContactCreate(accountId));
  };
  const createPaymentMethod = () => {
    navigate(ROUTES.getAccountPaymentMethodCreate(accountId));
  };

  const handleCloseAccountMergeModal = (navigateTo?: string) => {
    onAccountMergeModalClose();
    if (navigateTo) {
      navigate(navigateTo);
    }
  };

  const RelatedAccountTag = (
    <ExternalAssociationsIcons
      key="ExternalAssociationsIcons"
      entity={AssociationEntity.ACCOUNT}
      recordId={accountId}
      showWarningIfNotAssociated
    />
  );

  const pastDueBadges = showPastDue
    ? [
        <PastDueBadge
          key="past-due"
          pastDueInvoiceCount={accountDetails.pastDueInvoiceCount}
          pastDueAmount={accountDetails.pastDueAmountOutstanding}
        />,
      ]
    : [];

  return (
    <MPageContainer alignItems="stretch" enableAccountSearch>
      <MPageHeader
        hasBackButton
        status={AccountStatusDisplayText[accountDetails?.status]}
        title={accountDetails?.accountName}
        id={accountDetails?.id}
        copyUrl
        recordAuditInfo={accountDetails}
        entityJson={accountDetails}
        renderLeftSection={
          <MPageHeaderLeftSection
            hasBackButton
            status={AccountStatusDisplayText[accountDetails?.status]}
            title={accountDetails?.accountName}
            id={accountDetails?.id}
            copyUrl
            recordAuditInfo={accountDetails}
            entityJson={accountDetails}
            extraBadges={
              accountDetails.accountType === AccountTypeEnum.PARTNER
                ? [
                    ...pastDueBadges,
                    <AccountPartnerBadge key="partner-badge" />,
                    RelatedAccountTag,
                  ]
                : [...pastDueBadges, RelatedAccountTag]
            }
          />
        }
      >
        <MFlex align="center" gap="4" ml={2}>
          <AccountDetailsBillGroupFilter
            accountId={accountId}
            onChange={(value) => {
              if (value) {
                navigate(
                  ROUTES.getBillGroupPageRoute(`${value}`, billGroupTabName),
                );
              }
            }}
          />
          <MFlex gap="2" align="center">
            {accountHierarchy && (
              <MCustomIconButton
                variant="icon"
                btnSize={6}
                p={4}
                icon={MdAccountTree}
                aria-label="Account hierarchy"
                data-testid="account-hierarchy-btn"
                onClick={
                  canUpdateAccount ? onAccountHierarchyModalOpen : undefined
                }
              />
            )}
            <AccountHistoryDrawer accountId={accountId} disabled={!accountId} />
            <NotesDrawer
              id={accountDetails.id}
              entity="accounts"
              disabled={!accountDetails.id}
              isReadOnly={!canUpdateAccount}
            />
          </MFlex>

          <MFlex>
            <ACLCheck
              acls={[
                ['billing', 'create'],
                ['account_contacts', 'create'],
                ['sales', 'create'],
              ]}
            >
              <MDropdownActions
                popOverContentProps={{ minW: '150px' }}
                initialFocusRef={firstDropdownItemRef}
                menuButtonProps={{ asChild: true }}
                renderTrigger={() => (
                  <MButton
                    ref={newButtonRef}
                    variant="primary"
                    data-testid="new-account-subcat-btn"
                  >
                    New
                  </MButton>
                )}
              >
                <ACLCheck acls={[['account_contacts', 'create']]}>
                  <MDropdownActionItem
                    ref={contactItemRef}
                    onClick={createContact}
                  >
                    <DropDownMenuItem>Contact</DropDownMenuItem>
                  </MDropdownActionItem>
                </ACLCheck>
                <ACLCheck acls={[['billing', 'create']]}>
                  <MDropdownActionItem
                    ref={creditItemRef}
                    onClick={createCredit}
                  >
                    <DropDownMenuItem>Credit</DropDownMenuItem>
                  </MDropdownActionItem>
                </ACLCheck>
                <ACLCheck acls={[['billing', 'create']]}>
                  <MDropdownActionItem onClick={openNewAccountDocument}>
                    <DropDownMenuItem>Document</DropDownMenuItem>
                  </MDropdownActionItem>
                </ACLCheck>
                {/* <ACLCheck acls={[['billing', 'create']]}>
                <MDropdownActionItem onClick={createSubscription}>
                  <DropDownMenuItem>Subscription</DropDownMenuItem>
                </MDropdownActionItem>
              </ACLCheck> */}
                <ACLCheck acls={[['billing', 'create']]}>
                  <MDropdownActionItem
                    onClick={() =>
                      navigate(ROUTES.getOneTimeInvoiceCreateRoute(accountId))
                    }
                  >
                    <DropDownMenuItem>Onetime Invoice</DropDownMenuItem>
                  </MDropdownActionItem>
                </ACLCheck>
                <ACLCheck acls={[['billing', 'create']]}>
                  <MDropdownActionItem
                    onClick={paymentGateway ? createPaymentMethod : () => {}}
                    isDisabled={!paymentGateway}
                  >
                    <MFlex
                      alignItems="center"
                      color="inherit"
                      position="relative"
                    >
                      <DropDownMenuItem>Payment Method</DropDownMenuItem>

                      {!paymentGateway && (
                        <MTooltip
                          label="You need to set a payment gateway up in your tenant settings before you can add a new payment method."
                          placement="bottom-start"
                        >
                          <MFlex position="relative">
                            <MIcon as={MdInfo} color="tPurple.base" ml="5" />
                          </MFlex>
                        </MTooltip>
                      )}
                    </MFlex>
                  </MDropdownActionItem>
                </ACLCheck>
                <ACLCheck acls={[['billing', 'create']]}>
                  <MDropdownActionItem onClick={onManualPaymentModalOpen}>
                    <DropDownMenuItem>Payment</DropDownMenuItem>
                  </MDropdownActionItem>
                </ACLCheck>
                {canCreateQuote && (
                  <MDropdownActionItem
                    ref={quoteItemRef}
                    onClick={openNewQuote}
                  >
                    <DropDownMenuItem>Quote</DropDownMenuItem>
                  </MDropdownActionItem>
                )}
              </MDropdownActions>
            </ACLCheck>
            <DataTableActions
              buttonProps={{ ml: 2 }}
              actions={[
                {
                  title: 'Merge Account',
                  enabled:
                    enableAccountMerge && canDo([['accounts', 'update']]),
                  action: () => onAccountMergeModalOpen(),
                },
              ]}
            />
          </MFlex>
        </MFlex>
      </MPageHeader>
      <MFlex flex="1" ml={8} minH="0" flexDirection="column">
        <MTabs
          data-testid="account-detail-tabs"
          size="sm"
          index={activeTabIndex}
          // `MCustomLinkTab`s navigate themselves — see its doc comment.
          navigate={null}
        >
          <MTabList alignItems="flex-end" css={ACCOUNT_TAB_LIST_CSS}>
            {ACCOUNT_TAB_ROUTES.filter(
              ({ requiresFeatureFlag }) =>
                !(
                  requiresFeatureFlag &&
                  !featureFlags[requiresFeatureFlag as keyof FeatureFlag]
                ),
            ).map(({ route, title }, index) => {
              return (
                <MCustomLinkTab to={route} key={index}>
                  {title}
                </MCustomLinkTab>
              );
            })}
          </MTabList>
        </MTabs>

        <Routes>
          <Route
            path="/"
            element={
              <AccountOverview
                account={accountDetails}
                billGroupId={billGroupId}
              />
            }
          />
          <Route
            path={ROUTES.ACCOUNT_OVERVIEW}
            element={
              <AccountOverview
                account={accountDetails}
                billGroupId={billGroupId}
              />
            }
          />
          <Route
            path={ROUTES.ACCOUNT_DETAILS_TAB}
            element={
              <PrivateRoute
                acls={[['accounts', 'read']]}
                component={
                  <AccountDetailsTab
                    account={accountDetails}
                    refetchAccountDetails={refetchAccountDetails}
                  />
                }
              />
            }
          />
          <Route
            path={ROUTES.ACCOUNT_CONTACTS}
            element={
              <PrivateRoute
                acls={[['account_contacts', 'read']]}
                component={<ContactList />}
              />
            }
          />
          <Route
            path={ROUTES.ACCOUNT_CONTACTS_CREATE}
            element={
              <PrivateRoute
                acls={[['account_contacts', 'create']]}
                component={
                  <ContactFormModal
                    isOpen
                    accountId={accountId}
                    onClose={() =>
                      navigate(ROUTES.getAccountContactsRoute(accountId))
                    }
                  />
                }
              />
            }
          />
          <Route
            path={ROUTES.ACCOUNT_CONTACTS_EDIT}
            element={
              <PrivateRoute
                acls={[['account_contacts', 'update']]}
                component={
                  <ContactFormModal
                    isOpen
                    accountId={accountId}
                    onClose={() =>
                      navigate(ROUTES.getAccountContactsRoute(accountId))
                    }
                  />
                }
              />
            }
          />
          <Route
            path={ROUTES.ACCOUNT_CONTACTS_VIEW}
            element={
              <PrivateRoute
                acls={[['account_contacts', 'read']]}
                component={
                  <ContactFormModal
                    isOpen
                    isReadOnly
                    accountId={accountId}
                    onClose={() =>
                      navigate(ROUTES.getAccountContactsRoute(accountId))
                    }
                  />
                }
              />
            }
          />
          <Route
            path={ROUTES.ACCOUNT_BILL_GROUPS}
            element={
              <PrivateRoute
                acls={[['billing', 'read']]}
                component={<BillGroupListPage billGroupId={billGroupId} />}
              />
            }
          />
          <Route
            path={ROUTES.ACCOUNT_BILL_GROUP_VIEW}
            element={
              <PrivateRoute
                acls={[['billing', 'read']]}
                component={<BillGroupPage isReadOnly />}
              />
            }
          />
          {showSubscriptionOnAccount && (
            <Route
              path={ROUTES.ACCOUNT_SUBSCRIPTIONS}
              element={
                <PrivateRoute
                  acls={[['billing', 'read']]}
                  component={
                    <SubscriptionList
                      account={accountDetails}
                      billGroupId={billGroupId}
                    />
                  }
                />
              }
            />
          )}
          <Route
            path={ROUTES.ACCOUNT_USAGE}
            element={
              <PrivateRoute
                acls={[['billing', 'read']]}
                component={
                  <UsagePage
                    billGroupId={billGroupId}
                    account={accountDetails}
                  />
                }
              />
            }
          />
          <Route
            path={ROUTES.ACCOUNT_PAYMENT_METHODS}
            element={
              <PrivateRoute
                acls={[['billing', 'read']]}
                component={<PaymentMethodList />}
              />
            }
          />
          <Route
            path={ROUTES.ACCOUNT_PAYMENT_METHODS_CREATE}
            element={
              <PrivateRoute
                acls={[['billing', 'create']]}
                component={
                  <AddPaymentMethodForm
                    isOpen
                    accountId={accountId}
                    onClose={() => {
                      navigate(ROUTES.getAccountPaymentMethodRoute(accountId));
                    }}
                  />
                }
              />
            }
          />
          <Route
            path={ROUTES.ACCOUNT_PAYMENT_METHODS_VIEW}
            element={
              <PrivateRoute
                acls={[['billing', 'read']]}
                component={
                  <PaymentMethodModal
                    isOpen
                    onClose={() =>
                      navigate(ROUTES.getAccountPaymentMethodRoute(accountId))
                    }
                  />
                }
              />
            }
          />
          <Route
            path={ROUTES.ACCOUNT_PAYMENTS}
            element={
              <PrivateRoute
                acls={[['billing', 'read']]}
                component={
                  <PaymentList
                    account={accountDetails}
                    billGroupId={billGroupId}
                  />
                }
              />
            }
          />

          <Route
            path={ROUTES.ACCOUNT_CREDITS}
            element={
              <PrivateRoute
                acls={[['billing', 'read']]}
                component={
                  <CreditsList
                    account={accountDetails}
                    billGroupId={billGroupId}
                  />
                }
              />
            }
          />
          <Route
            path={ROUTES.ACCOUNT_CREDITS_CREATE}
            element={
              <PrivateRoute
                acls={[['billing', 'create']]}
                component={
                  <CreditFormCreateModal
                    isOpen
                    account={accountDetails}
                    onClose={() =>
                      navigate(ROUTES.getAccountCreditsRoute(accountId))
                    }
                  />
                }
              />
            }
          />

          <Route
            path={ROUTES.ACCOUNT_QUOTES}
            element={
              <PrivateRoute
                acls={[['account_quotes', 'read']]}
                component={
                  <QuoteListAccount
                    account={accountDetails}
                    billGroupId={billGroupId}
                  />
                }
              />
            }
          />
          <Route
            path={`${ROUTES.ACCOUNT_CONTRACTS}/*`}
            element={
              <PrivateRoute
                acls={[['account_quotes', 'read']]}
                component={
                  <ContractListAccount
                    account={accountDetails}
                    billGroupId={billGroupId}
                  />
                }
              />
            }
          >
            <Route
              path={ROUTES.CONTRACT_EDIT}
              element={
                <PrivateRoute
                  acls={[['account_quotes', 'read']]}
                  component={
                    <ContractModal
                      isOpen
                      onClose={() =>
                        navigate(ROUTES.getAccountContractsRoute(accountId))
                      }
                    />
                  }
                />
              }
            />
          </Route>
          <Route
            path={ROUTES.ACCOUNT_INVOICES}
            element={
              <PrivateRoute
                acls={[['billing', 'read']]}
                component={
                  <AccountInvoices
                    account={accountDetails}
                    billGroupId={billGroupId}
                  />
                }
              />
            }
          />

          <Route
            path={ROUTES.ACCOUNT_DOCUMENTS}
            element={
              <PrivateRoute
                acls={[['billing', 'read']]}
                component={<AccountDocumentList account={accountDetails} />}
              />
            }
          />
          <Route
            path={ROUTES.ACCOUNT_DOCUMENTS_CREATE}
            element={
              <PrivateRoute
                acls={[['billing', 'create']]}
                component={
                  <AccountDocumentFormModal
                    isOpen
                    account={accountDetails}
                    onClose={() =>
                      navigate(ROUTES.getAccountDocumentList(accountId))
                    }
                  />
                }
              />
            }
          />

          <Route
            path={ROUTES.ACCOUNT_REFUNDS}
            element={
              <PrivateRoute
                acls={[['billing', 'read']]}
                component={<AccountRefundList account={accountDetails} />}
              />
            }
          />
        </Routes>
      </MFlex>
      {accountHierarchy && accountDetails && isAccountHierarchyModalOpen && (
        <AccountHierarchyModal
          accountId={accountId}
          isReadOnly={!canUpdateAccount}
          onClose={onAccountHierarchyModalClose}
        />
      )}
      {accountDetails && isAccountMergeModalOpen && (
        <AccountMergeModal
          account={accountDetails}
          onClose={handleCloseAccountMergeModal}
        />
      )}
      {isManualPaymentModalOpen && (
        <AccountManualPaymentModal
          accountId={accountId}
          accountCurrency={accountDetails.defaultCurrency}
          isOpen={isManualPaymentModalOpen}
          onClose={onManualPaymentModalClose}
        />
      )}
    </MPageContainer>
  );
};
